/**
 * InputErrors Component
 * Responsible for showing input level errors specific to a particular field
 *
 * Params:
 * input: The field that the errors will show for
 */
import {
  Component,
  ChangeDetectorRef,
  ElementRef,
  Injector,
  inject,
  input
} from '@angular/core';
import { ViewportRuler } from '@angular/cdk/scrolling';

import { FORM_SHELL } from '../form-shell.contract';
import { InputGroupComponent } from '../input-group/input-group.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { fieldStateFromNgModel } from '../field-state/field-state';

import type { OnInit, OnDestroy } from '@angular/core';
import type { NgModel, ValidationErrors } from '@angular/forms';
import type { Subscription } from 'rxjs';

import type { InputValidationError } from '../domain/form.domain';
import type { InterpolateParams } from '../../../pipes/translate.pipe';
import type { FieldState } from '../field-state/field-state';
import type { FormShell } from '../form-shell.contract';

@Component({
  selector: 'td-wb-input-errors',
  templateUrl: 'input-errors.component.html',
  styleUrls: ['input-errors.component.scss'],
  imports: [TranslatePipe],
  host: {
    '[class.td-wb-input-errors--multi-line]': 'multiLine()',
    '[class.td-wb-input-errors--multi-line--spaced]': 'multiLine() && multiLineSpaceReserve()'
  }
})
export class InputErrorsComponent implements OnInit, OnDestroy {
  /**
   * @param errorKeyNamespace
   * @description A dot-separated string that maps to a namespace entry on the
   * corresponding language's locale file
   * @example <td-wb-input-errors errorKeyNamespace='tdWb.form.age'></td-wb-input-errors>
   */
  readonly errorKeyNamespace = input<string>(undefined);
  /**
   * @param errorKeyNamespaceParams
   * @description An object containing a value that is to be interpolated by the tdWbTranslate params
   * @example <td-wb-input-errors errorKeyNamespace='tdWb.form.age' [errorKeyNamespaceParams]='{min: 18, max: 65s}'></td-wb-input-errors>
   * Locale string value is "max": "Age should be below {{max}} years old."
   * Final result is "Age should be below 65 years old"
   */
  readonly errorKeyNamespaceParams = input<InterpolateParams>({});
  readonly errorKeyDisplayBeforeSubmit = input<Array<string>>(undefined);
  readonly displayErrorForPristineState = input<boolean>(undefined);
  readonly align = input<'left' | 'right'>('left');
  readonly multiLine = input<boolean>(undefined);
  readonly multiLineSpaceReserve = input<boolean>(true);
  readonly hasInputNote = input<boolean>(false);

  private statusAdapter: FieldState | null = null;
  private statusChangesSubscription: Subscription;
  private displayBeforeSubmitErrorKeys: Record<string, unknown> = {};
  private errors: Array<InputValidationError> = [];
  private forceDisplay: boolean = false;

  private readonly formShell = inject<FormShell | null>(FORM_SHELL, { optional: true });
  private readonly inputGroup = inject(InputGroupComponent, { host: true, optional: true });
  private readonly elementRef = inject(ElementRef);
  private readonly viewportRuler = inject(ViewportRuler);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);

  ngOnInit(): void {
    const errorKeyDisplayBeforeSubmit = this.errorKeyDisplayBeforeSubmit();
    if (errorKeyDisplayBeforeSubmit instanceof Array) {
      errorKeyDisplayBeforeSubmit.forEach((errorKey: string): void => {
        this.displayBeforeSubmitErrorKeys[errorKey] = true;
      });
    }
  }

  ngOnDestroy(): void {
    if (this.statusChangesSubscription !== undefined) {
      this.statusChangesSubscription.unsubscribe();
      if (this.statusAdapter !== null) {
        this.formShell?.setAccessibleError(this.statusAdapter.name(), undefined);
      }
    }
  }

  subscribeToStatusChange(inputControl: NgModel): void {
    this.subscribeToStateAdapter(fieldStateFromNgModel(inputControl, this.injector));
  }

  subscribeToStateAdapter(stateAdapter: FieldState): void {
    this.statusAdapter = stateAdapter;
    if (this.statusChangesSubscription === undefined) {
      this.statusChangesSubscription = stateAdapter.subscribeStatusChange((isInvalid: boolean): void => {
        this.changeDetectorRef.markForCheck();
        this.handleStatusChanges(isInvalid);
      });
    }
  }

  shouldForceDisplay(): boolean {
    if (!this.displayErrorForPristineState() && this.isControlPristine()) {
      return false;
    }
    return this.forceDisplay;
  }

  handleStatusChanges(isInvalid: boolean): void {
    this.onInputErrorsChange(this.statusAdapter?.validationErrors(this.errorKeyNamespace() ?? ''));

    if (this.formShell !== null && this.statusAdapter !== null) {
      this.errors.forEach((error: InputValidationError): void => {
        error.isDisplayed = isInvalid && (this.shouldForceDisplay() || this.isSubmitted());
      });
      this.formShell.setAccessibleError(this.statusAdapter.name(), this.errors.length > 0 ? this.errors[0] : undefined);
    }
  }

  isErrorDisplayed(): boolean {
    // display error only for submitted values and forced display errors
    // do not display errors when submitted value were reset to undefined or null
    const hasValue = this.statusAdapter?.hasValue() ?? false;
    const isFormSubmitted = this.isSubmitted();
    const hasErrors = this.errors.length > 0;
    const shouldDisplayForcefully = this.shouldForceDisplay();
    const isTouchedSignalField = this.statusAdapter?.kind === 'signalFieldState' && this.statusAdapter.touched();

    return (hasValue && isFormSubmitted && hasErrors) || (isTouchedSignalField && hasErrors) || shouldDisplayForcefully;
  }

  isSubmitted(): boolean {
    if (this.statusAdapter !== null && this.formShell !== null) {
      const submissionAttempted = this.formShell.shellStatus().submissionAttempted;
      return this.statusAdapter.kind === 'signalFieldState'
        ? submissionAttempted || this.statusAdapter.touched()
        : submissionAttempted && this.statusAdapter.hasSubmittedFlag();
    }
    return false;
  }

  onInputErrorsChange(inputErrors?: ValidationErrors | Array<{ key: string; messageKey: string }>): void {
    this.errors = [];
    let errorToDisplay: InputValidationError;

    if (inputErrors !== undefined && inputErrors !== null && inputErrors.length > 0) {
      this.forceDisplay = this.formShell !== null && this.isSubmitted();
      errorToDisplay = this.getErrorToDisplay(inputErrors);
      if (errorToDisplay !== undefined) {
        this.errors.push(errorToDisplay);
      }
    }
  }

  getErrorToDisplay(inputErrors: ValidationErrors | Array<{ key: string; messageKey: string }>): InputValidationError {
    let errorToDisplay: InputValidationError;
    let i = 0;
    if (inputErrors !== undefined && inputErrors !== null) {
      const normalizedErrors = Array.isArray(inputErrors)
        ? inputErrors
        : Object.keys(inputErrors).map((key: string): { key: string; messageKey: string } => ({
          key,
          messageKey: `${this.errorKeyNamespace()}.${key}`
        }));
      normalizedErrors.forEach((error: { key: string; messageKey: string }): void => {
        const key = error.key;
        if (i === 0 || this.displayBeforeSubmitErrorKeys[key]) {
          this.forceDisplay = this.forceDisplay || this.isDisplayBeforeSubmit(key);
          errorToDisplay = this.getError(key, error.messageKey);
        }
        i++;
      });
    }
    return errorToDisplay;
  }

  getErrors(): Array<InputValidationError> {
    return this.errors;
  }

  singleLineError(): boolean {
    return !this.multiLine();
  }

  scrollIntoView(): void {
    if (this.elementRef.nativeElement !== null) {
      this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
    this.focusInput();
  }

  focusInput(): void {
    if ((this.inputGroup ?? null) !== null) {
      this.inputGroup.focusInput();
    }
  }

  private getError(key: string, messageKey: string): InputValidationError {
    return {
      key,
      messageKey,
      isDisplayed: this.isDisplayBeforeSubmit(key) || this.isErrorDisplayed(),
      scrollIntoView: (): void => this.scrollIntoView(),
      isInViewPort: (): boolean => this.isVisible(),
      focusInput: (): void => this.focusInput()
    };
  }

  private isVisible(): boolean {
    if (this.elementRef.nativeElement !== null) {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      const viewPortRect = this.viewportRuler.getViewportRect();
      return rect.top >= 0 && rect.left >= 0 && rect.bottom < viewPortRect.height && rect.right < viewPortRect.width;
    }
    return true;
  }

  private isDisplayBeforeSubmit(key: string): boolean {
    return this.displayBeforeSubmitErrorKeys[key] !== undefined && this.displayBeforeSubmitErrorKeys[key] !== null;
  }

  private isControlPristine(): boolean {
    return this.statusAdapter !== null && this.statusAdapter.isPristineUntouched();
  }
}
